/**
 * Safe Fetch Wrapper with Exponential Backoff
 * 
 * P0 BULLETPROOF: Enhanced for 10+ concurrent users
 * 
 * Features:
 * - Prevents "Response body is already used" errors from rrweb/monitoring tools
 * - Exponential backoff retry for 502/503/520 errors
 * - Max 7 retries with longer waits for overloaded servers
 * - Falls back to XHR when fetch interceptors cause issues
 * - P0 HOTFIX: Adds X-App-Version header on all writes
 * - P0 STABILITY: Request deduplication to prevent duplicate calls
 * - P0 BULLETPROOF: Offline queue for failed write requests
 * - P0 BULLETPROOF: Auto-recovery with health polling
 * 
 * Usage:
 *   const { ok, status, data, text, error } = await safeFetch('/api/endpoint', options);
 */

import { BUILD_VERSION } from '../buildVersion';

// Track failed endpoints for circuit breaker
const failedEndpoints = new Map();
const CIRCUIT_BREAKER_TIMEOUT = 60000; // 60 seconds - longer recovery time
const CIRCUIT_BREAKER_THRESHOLD = 10; // More failures before tripping

// P0 STABILITY: Request deduplication - prevent duplicate concurrent requests
const pendingRequests = new Map();
const REQUEST_DEDUPE_WINDOW = 500; // ms - requests within this window are deduplicated

// P0 BULLETPROOF: Offline queue for failed writes
const offlineWriteQueue = [];
const MAX_OFFLINE_QUEUE = 50;
let isProcessingOfflineQueue = false;
let serverHealthy = true;

// P0 BULLETPROOF: Health check polling
let healthCheckInterval = null;
const HEALTH_CHECK_INTERVAL = 10000; // 10 seconds when server is unhealthy

/**
 * Perform fetch using XMLHttpRequest (bypasses fetch interceptors)
 */
function xhrFetch(url, options = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const method = options.method || 'GET';
    
    xhr.open(method, url, true);
    
    // P0 HOTFIX: Add X-App-Version header for write requests
    const isWriteRequest = ['PUT', 'POST', 'DELETE', 'PATCH'].includes(method.toUpperCase());
    if (isWriteRequest && BUILD_VERSION) {
      xhr.setRequestHeader('X-App-Version', BUILD_VERSION);
    }
    
    // Set headers
    if (options.headers) {
      const headers = options.headers;
      if (headers instanceof Headers) {
        headers.forEach((value, key) => xhr.setRequestHeader(key, value));
      } else if (typeof headers === 'object') {
        Object.entries(headers).forEach(([key, value]) => {
          if (value) xhr.setRequestHeader(key, value);
        });
      }
    }
    
    // Handle abort signal
    if (options.signal) {
      options.signal.addEventListener('abort', () => xhr.abort());
    }
    xhr.timeout = options.timeout || 30000; // 30 second default timeout
    
    xhr.onload = function() {
      const headers = {};
      const headerStr = xhr.getAllResponseHeaders();
      if (headerStr) {
        headerStr.split('\r\n').forEach(line => {
          const parts = line.split(': ');
          if (parts[0]) headers[parts[0].toLowerCase()] = parts[1];
        });
      }
      
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        text: xhr.responseText,
        headers,
        url: xhr.responseURL || url
      });
    };
    
    xhr.onerror = function() {
      reject(new Error('Network request failed'));
    };
    
    xhr.ontimeout = function() {
      reject(new Error('Request timeout'));
    };
    
    xhr.onabort = function() {
      reject(new Error('Request aborted'));
    };
    
    // Send body if present
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
}

/**
 * Check if endpoint is in circuit breaker state
 */
function isCircuitOpen(url) {
  const baseUrl = url.split('?')[0];
  const failure = failedEndpoints.get(baseUrl);
  if (!failure) return false;
  
  if (Date.now() - failure.timestamp > CIRCUIT_BREAKER_TIMEOUT) {
    failedEndpoints.delete(baseUrl);
    return false;
  }
  return failure.count >= CIRCUIT_BREAKER_THRESHOLD;
}

/**
 * Record endpoint failure
 */
function recordFailure(url) {
  const baseUrl = url.split('?')[0];
  const existing = failedEndpoints.get(baseUrl) || { count: 0, timestamp: Date.now() };
  failedEndpoints.set(baseUrl, {
    count: existing.count + 1,
    timestamp: Date.now()
  });
}

/**
 * Clear endpoint failure (on success)
 */
function clearFailure(url) {
  const baseUrl = url.split('?')[0];
  failedEndpoints.delete(baseUrl);
}

/**
 * Determine if error is retryable
 */
function isRetryableError(status) {
  // Retry on gateway errors and rate limits
  return [502, 503, 504, 520, 521, 522, 523, 524, 429].includes(status);
}

/**
 * Sleep helper for backoff
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Notify UI about retry attempts
 */
function notifyRetry(url, attempt, maxRetries, status) {
  window.dispatchEvent(new CustomEvent('apiRetry', { 
    detail: { 
      url: url.split('?')[0], 
      attempt, 
      maxRetries,
      status,
      message: `Serveur occupé (${status}), nouvelle tentative ${attempt}/${maxRetries}...`
    } 
  }));
}

/**
 * Safe fetch wrapper that handles response body reading once
 * Falls back to XHR if fetch fails with "body already used" error
 * 
 * P0 BULLETPROOF: Enhanced retry logic for production stability
 * 
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} options.maxRetries - Max retries for 5xx errors (default: 7)
 * @param {boolean} options.skipRetry - Skip retry logic entirely
 * @param {number} options.timeout - Request timeout in ms (default: 25000)
 * @param {boolean} options.queueOnFailure - Queue write for retry when server recovers (default: true for writes)
 */
export async function safeFetch(url, options = {}) {
  const maxRetries = options.maxRetries ?? 7; // Increased to 7 retries for bulletproof
  const skipRetry = options.skipRetry ?? false;
  const skipDedupe = options.skipDedupe ?? false;
  const timeout = options.timeout ?? 25000; // 25s default
  let lastError = null;
  let lastStatus = 0;
  
  // P0 STABILITY: Request deduplication for GET requests
  const method = (options.method || 'GET').toUpperCase();
  const isWriteRequest = ['PUT', 'POST', 'DELETE', 'PATCH'].includes(method);
  const dedupeKey = `${method}:${url}`;
  
  if (!skipDedupe && method === 'GET') {
    const pending = pendingRequests.get(dedupeKey);
    if (pending && Date.now() - pending.timestamp < REQUEST_DEDUPE_WINDOW) {
      // Return the same promise for duplicate request
      console.log(`🔄 Deduped request: ${url}`);
      return pending.promise;
    }
  }
  
  // Check circuit breaker (but allow health check endpoints)
  const isHealthCheck = url.includes('/health') || url.includes('/system/status');
  if (!isHealthCheck && isCircuitOpen(url)) {
    console.warn(`⚡ Circuit breaker open for: ${url}`);
    
    // P0 BULLETPROOF: Queue write requests for later
    if (isWriteRequest && options.queueOnFailure !== false) {
      queueOfflineWrite(url, options);
      return {
        ok: false,
        status: 503,
        statusText: 'Queued',
        data: null,
        text: '',
        error: 'Request queued - will retry when server recovers',
        headers: {},
        url,
        queued: true
      };
    }
    
    return {
      ok: false,
      status: 503,
      statusText: 'Circuit Open',
      data: null,
      text: '',
      error: 'Service temporarily unavailable - please retry in a moment',
      headers: {},
      url,
      circuitBreaker: true
    };
  }
  
  // Create the actual fetch promise
  const fetchPromise = (async () => {
  for (let attempt = 0; attempt <= (skipRetry ? 0 : maxRetries); attempt++) {
    // P0 BULLETPROOF: Longer exponential backoff: 0, 2s, 4s, 8s, 15s, 25s, 40s
    if (attempt > 0) {
      const backoffMs = Math.min(2000 * Math.pow(1.8, attempt - 1), 40000);
      // Notify UI about retry
      notifyRetry(url, attempt, maxRetries, lastStatus);
      await sleep(backoffMs);
    }
    
    const result = await doFetch(url, { ...options, timeout });
    
    if (result.ok) {
      clearFailure(url);
      serverHealthy = true;
      stopHealthPolling();
      
      // Notify UI about successful recovery if we had to retry
      if (attempt > 0) {
        window.dispatchEvent(new CustomEvent('apiSuccess', { 
          detail: { url: url.split('?')[0], attempts: attempt + 1 } 
        }));
      }
      return result;
    }
    
    lastError = result.error;
    lastStatus = result.status;
    
    // Don't retry on auth errors or client errors (except 429)
    if (result.status >= 400 && result.status < 500 && result.status !== 429) {
      return result;
    }
    
    // Don't retry if not a retryable error
    if (!isRetryableError(result.status)) {
      return result;
    }
    
    // Record failure for circuit breaker
    if (isRetryableError(result.status)) {
      recordFailure(url);
      serverHealthy = false;
      startHealthPolling();
    }
  }
  
  // All retries exhausted
  // P0 BULLETPROOF: Queue failed writes
  if (isWriteRequest && options.queueOnFailure !== false) {
    queueOfflineWrite(url, options);
    return {
      ok: false,
      status: lastStatus,
      statusText: 'Queued After Retries',
      data: null,
      text: '',
      error: 'Request saved - will retry when server recovers',
      headers: {},
      url,
      queued: true,
      retriesExhausted: true
    };
  }
  
  return {
    ok: false,
    status: lastStatus,
    statusText: 'Retries Exhausted',
    data: null,
    text: '',
    error: lastError || `Request failed after ${maxRetries + 1} attempts`,
    headers: {},
    url,
    retriesExhausted: true
  };
  })();
  
  // P0 STABILITY: Store pending request for deduplication
  if (!skipDedupe && method === 'GET') {
    pendingRequests.set(dedupeKey, { promise: fetchPromise, timestamp: Date.now() });
    // Clean up after request completes
    fetchPromise.finally(() => {
      setTimeout(() => pendingRequests.delete(dedupeKey), REQUEST_DEDUPE_WINDOW);
    });
  }
  
  return fetchPromise;
}

/**
 * Internal fetch implementation
 */
async function doFetch(url, options = {}) {
  let response;
  let useXhr = false;
  
  // P0 HOTFIX: Add X-App-Version header for write requests (PUT, POST, DELETE, PATCH)
  const method = (options.method || 'GET').toUpperCase();
  const isWriteRequest = ['PUT', 'POST', 'DELETE', 'PATCH'].includes(method);
  
  if (isWriteRequest && BUILD_VERSION) {
    options.headers = {
      ...options.headers,
      'X-App-Version': BUILD_VERSION
    };
  }
  
  // First, try regular fetch
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || 30000);
    
    const fetchOptions = {
      ...options,
      signal: options.signal || controller.signal
    };
    
    response = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);
    
    // Extract status info immediately
    const ok = response.ok;
    const status = response.status;
    const statusText = response.statusText;
    const responseUrl = response.url;
    
    // Try to read headers
    const headers = {};
    try {
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } catch (e) {
      // Ignore header errors
    }
    
    // Try to read body - this is where "already used" errors occur
    let text = '';
    try {
      text = await response.text();
    } catch (bodyErr) {
      if (bodyErr.message && (bodyErr.message.includes('already') || bodyErr.message.includes('clone'))) {
        // Body was consumed by monitoring tool - fall back to XHR
        useXhr = true;
      } else {
        // Some other error reading body
        console.warn('[safeFetch] Body read error:', bodyErr.message);
      }
    }
    
    if (!useXhr) {
      // Successfully read with fetch
      let data = null;
      const contentType = headers['content-type'] || '';
      
      if (text && (contentType.includes('json') || text.startsWith('{') || text.startsWith('['))) {
        try {
          data = JSON.parse(text);
        } catch (e) {
          // Not valid JSON
        }
      }
      
      let error = null;
      if (!ok) {
        error = data?.detail || data?.error || data?.message || 
                (text && text.length < 200 ? text : `HTTP ${status}`);
      }
      
      return { ok, status, statusText, data, text, error, headers, url: responseUrl };
    }
  } catch (fetchErr) {
    if (fetchErr.name === 'AbortError') {
      return {
        ok: false,
        status: 0,
        statusText: 'Timeout',
        data: null,
        text: '',
        error: 'Request timed out',
        headers: {},
        url
      };
    }
    
    if (fetchErr.message && (fetchErr.message.includes('already') || fetchErr.message.includes('clone'))) {
      useXhr = true;
    } else {
      // Network error
      return {
        ok: false,
        status: 0,
        statusText: 'Network Error',
        data: null,
        text: '',
        error: fetchErr.message || 'Network connection failed',
        headers: {},
        url
      };
    }
  }
  
  // Fall back to XHR
  if (useXhr) {
    try {
      const xhrResult = await xhrFetch(url, options);
      
      let data = null;
      const contentType = xhrResult.headers['content-type'] || '';
      const text = xhrResult.text || '';
      
      if (text && (contentType.includes('json') || text.startsWith('{') || text.startsWith('['))) {
        try {
          data = JSON.parse(text);
        } catch (e) {
          // Not valid JSON
        }
      }
      
      let error = null;
      if (!xhrResult.ok) {
        error = data?.detail || data?.error || data?.message || 
                (text && text.length < 200 ? text : `HTTP ${xhrResult.status}`);
      }
      
      return {
        ok: xhrResult.ok,
        status: xhrResult.status,
        statusText: xhrResult.statusText,
        data,
        text,
        error,
        headers: xhrResult.headers,
        url: xhrResult.url
      };
    } catch (xhrErr) {
      return {
        ok: false,
        status: 0,
        statusText: 'XHR Error',
        data: null,
        text: '',
        error: xhrErr.message || 'XHR request failed',
        headers: {},
        url
      };
    }
  }
  
  // Should never reach here
  return {
    ok: false,
    status: 0,
    statusText: 'Unknown Error',
    data: null,
    text: '',
    error: 'Unknown error occurred',
    headers: {},
    url
  };
}

/**
 * Safe fetch with automatic authorization header
 * Reads token from localStorage and adds to request headers
 * 
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (same as safeFetch)
 * @returns {Promise<object>} - Response object with { ok, status, data, text, error, headers }
 */
export async function safeFetchAuth(url, options = {}) {
  // Read token fresh from localStorage for each request
  const token = localStorage.getItem('token');
  const headers = {
    ...(options.headers || {}),
  };
  
  // Log the request for debugging
  const method = options.method || 'GET';
  console.log(`🔐 safeFetchAuth: ${method} ${url.split('?')[0]}`);
  
  // Validate token exists
  if (!token) {
    console.error('❌ safeFetchAuth: No token in localStorage - user needs to login');
    // Dispatch event to notify app of auth failure
    window.dispatchEvent(new CustomEvent('authError', { detail: { type: 'noToken', url } }));
    return {
      ok: false,
      status: 401,
      statusText: 'Unauthorized',
      data: null,
      text: '',
      error: 'No authentication token - please login again',
      headers: {},
      url,
      noToken: true
    };
  }
  
  // Validate token is not a garbage value
  if (token === 'undefined' || token === 'null' || token.trim() === '') {
    console.error('❌ safeFetchAuth: Token is invalid string value - clearing');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new CustomEvent('authError', { detail: { type: 'invalidValue', url } }));
    return {
      ok: false,
      status: 401,
      statusText: 'Invalid Token',
      data: null,
      text: '',
      error: 'Invalid authentication token - please login again',
      headers: {},
      url,
      invalidToken: true
    };
  }
  
  // Check token format (JWT should have 3 parts separated by dots)
  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) {
    console.error(`❌ safeFetchAuth: Invalid token format (${tokenParts.length} parts, expected 3) - clearing`);
    console.error(`   Token preview: ${token.substring(0, 20)}...`);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new CustomEvent('authError', { detail: { type: 'invalidFormat', url } }));
    return {
      ok: false,
      status: 401,
      statusText: 'Invalid Token',
      data: null,
      text: '',
      error: 'Invalid authentication token format - please login again',
      headers: {},
      url,
      invalidToken: true
    };
  }
  
  // Token looks valid, set the header
  headers['Authorization'] = `Bearer ${token}`;
  
  // Make the request
  const result = await safeFetch(url, {
    ...options,
    headers
  });
  
  // If we got a 401 from the server, the token might be expired
  if (result.status === 401) {
    console.warn('⚠️ safeFetchAuth: Server returned 401 - token may be expired');
    window.dispatchEvent(new CustomEvent('authError', { detail: { type: 'serverReject', url, status: 401 } }));
  }
  
  // PRODUCTION SAFETY: Handle version conflicts (optimistic locking)
  if (result.status === 409) {
    console.warn('🔒 safeFetchAuth: Version conflict - data was modified by another user');
    window.dispatchEvent(new CustomEvent('versionConflict', { 
      detail: { 
        url, 
        message: result.data?.detail || 'Data was modified by another user. Please refresh and try again.' 
      } 
    }));
  }
  
  return result;
}

/**
 * Wrapper for axios-like interface
 * Use this when you need axios compatibility
 */
export function createAxiosLikeClient() {
  return {
    async get(url, config = {}) {
      const result = await safeFetch(url, {
        method: 'GET',
        headers: config.headers,
        maxRetries: config.maxRetries,
        skipRetry: config.skipRetry,
        timeout: config.timeout
      });
      
      if (!result.ok) {
        const error = new Error(result.error || `Request failed with status ${result.status}`);
        error.response = { status: result.status, data: result.data };
        throw error;
      }
      
      return { data: result.data, status: result.status, headers: result.headers };
    },
    
    async post(url, data, config = {}) {
      const result = await safeFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...config.headers
        },
        body: typeof data === 'string' ? data : JSON.stringify(data),
        maxRetries: config.maxRetries,
        skipRetry: config.skipRetry,
        timeout: config.timeout
      });
      
      if (!result.ok) {
        const error = new Error(result.error || `Request failed with status ${result.status}`);
        error.response = { status: result.status, data: result.data };
        throw error;
      }
      
      return { data: result.data, status: result.status, headers: result.headers };
    },
    
    async put(url, data, config = {}) {
      const result = await safeFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...config.headers
        },
        body: typeof data === 'string' ? data : JSON.stringify(data),
        maxRetries: config.maxRetries,
        skipRetry: config.skipRetry,
        timeout: config.timeout
      });
      
      if (!result.ok) {
        const error = new Error(result.error || `Request failed with status ${result.status}`);
        error.response = { status: result.status, data: result.data };
        throw error;
      }
      
      return { data: result.data, status: result.status, headers: result.headers };
    },
    
    async delete(url, config = {}) {
      const result = await safeFetch(url, {
        method: 'DELETE',
        headers: config.headers,
        maxRetries: config.maxRetries,
        skipRetry: config.skipRetry,
        timeout: config.timeout
      });
      
      if (!result.ok) {
        const error = new Error(result.error || `Request failed with status ${result.status}`);
        error.response = { status: result.status, data: result.data };
        throw error;
      }
      
      return { data: result.data, status: result.status, headers: result.headers };
    }
  };
}

/**
 * Check if an endpoint is healthy
 */
export function isEndpointHealthy(url) {
  return !isCircuitOpen(url);
}

/**
 * Reset circuit breaker for an endpoint
 */
export function resetCircuitBreaker(url) {
  clearFailure(url);
}

/**
 * Reset all circuit breakers
 */
export function resetAllCircuitBreakers() {
  failedEndpoints.clear();
}

// ============================================================================
// P0 BULLETPROOF: Offline Queue & Auto-Recovery
// ============================================================================

/**
 * Queue a failed write request for retry when server recovers
 */
function queueOfflineWrite(url, options) {
  if (offlineWriteQueue.length >= MAX_OFFLINE_QUEUE) {
    // Remove oldest item if queue is full
    offlineWriteQueue.shift();
  }
  
  offlineWriteQueue.push({
    url,
    options: { ...options },
    timestamp: Date.now(),
    retryCount: 0
  });
  
  console.log(`📦 Queued offline write: ${url} (${offlineWriteQueue.length} in queue)`);
  
  // Notify UI
  window.dispatchEvent(new CustomEvent('offlineQueueUpdate', {
    detail: { queueLength: offlineWriteQueue.length, action: 'added', url }
  }));
}

/**
 * Process offline queue when server recovers
 */
async function processOfflineQueue() {
  if (isProcessingOfflineQueue || offlineWriteQueue.length === 0) return;
  
  isProcessingOfflineQueue = true;
  console.log(`🔄 Processing offline queue (${offlineWriteQueue.length} items)`);
  
  while (offlineWriteQueue.length > 0 && serverHealthy) {
    const item = offlineWriteQueue[0];
    
    try {
      const result = await doFetch(item.url, {
        ...item.options,
        timeout: 25000
      });
      
      if (result.ok) {
        offlineWriteQueue.shift(); // Remove successful item
        console.log(`✅ Offline write succeeded: ${item.url}`);
        window.dispatchEvent(new CustomEvent('offlineQueueUpdate', {
          detail: { queueLength: offlineWriteQueue.length, action: 'processed', url: item.url, success: true }
        }));
      } else if (isRetryableError(result.status)) {
        // Server still unhealthy, stop processing
        serverHealthy = false;
        break;
      } else {
        // Permanent failure, remove from queue
        offlineWriteQueue.shift();
        console.warn(`❌ Offline write permanently failed: ${item.url}`);
        window.dispatchEvent(new CustomEvent('offlineQueueUpdate', {
          detail: { queueLength: offlineWriteQueue.length, action: 'failed', url: item.url }
        }));
      }
    } catch (e) {
      // Network error, stop processing
      serverHealthy = false;
      break;
    }
    
    // Small delay between requests to avoid overwhelming server
    await sleep(500);
  }
  
  isProcessingOfflineQueue = false;
}

/**
 * Start health polling when server is unhealthy
 */
function startHealthPolling() {
  if (healthCheckInterval) return;
  
  console.log('🏥 Starting health polling...');
  
  healthCheckInterval = setInterval(async () => {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const result = await doFetch(`${apiUrl}/api/health`, {
        timeout: 5000,
        skipRetry: true
      });
      
      if (result.ok) {
        console.log('✅ Server recovered!');
        serverHealthy = true;
        stopHealthPolling();
        resetAllCircuitBreakers();
        processOfflineQueue();
        
        // Notify UI
        window.dispatchEvent(new CustomEvent('serverRecovered', {
          detail: { queueLength: offlineWriteQueue.length }
        }));
      }
    } catch (e) {
      // Server still down
    }
  }, HEALTH_CHECK_INTERVAL);
}

/**
 * Stop health polling
 */
function stopHealthPolling() {
  if (healthCheckInterval) {
    clearInterval(healthCheckInterval);
    healthCheckInterval = null;
    console.log('🏥 Stopped health polling');
  }
}

/**
 * Get offline queue status
 */
export function getOfflineQueueStatus() {
  return {
    length: offlineWriteQueue.length,
    items: offlineWriteQueue.map(item => ({
      url: item.url,
      method: item.options?.method || 'GET',
      timestamp: item.timestamp
    })),
    serverHealthy,
    isProcessing: isProcessingOfflineQueue
  };
}

/**
 * Manually trigger queue processing
 */
export function retryOfflineQueue() {
  serverHealthy = true;
  processOfflineQueue();
}

/**
 * Clear offline queue
 */
export function clearOfflineQueue() {
  offlineWriteQueue.length = 0;
  window.dispatchEvent(new CustomEvent('offlineQueueUpdate', {
    detail: { queueLength: 0, action: 'cleared' }
  }));
}

export default safeFetch;
